/**
 * TenantStrumentiTab — la linguetta «Strumenti» della scheda azienda (atlante cap. 251, tappa 4).
 *
 * Per ogni strumento a canone acceso: come lo paga l'azienda, il consumo del periodo in corso con l'extra e il tetto che
 * ha scelto (si vede, non si cambia: è una decisione dell'azienda), e i periodi passati. Per ogni strumento, acceso o
 * no, le sue CONDIZIONI: listino, prezzo di prima, o su misura (con la nota del perché), e i bottoni per darne di sue o
 * tornare al listino (`DialogoSuMisura`, `consoleCondizioniSuMisura`). Le condizioni sono quelle che l'azienda paga
 * davvero (`strumentoPerLAzienda`).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import type { StrumentoACanone } from '@shared/strumenti/listino';
import { consumoDelPeriodo, extraInFattura, righeDiConsumo, type RigaDiConsumo } from '@shared/strumenti/consumoDellAzienda';
import { extraInCentesimi, soloAConsumo, strumentoPerLAzienda } from '@shared/strumenti/listino';
import { suMisuraDallAzienda } from '@shared/strumenti/suMisura';
import { regaloDallAzienda } from '@shared/strumenti/regalo';
import { tettoDelloStrumento, tettoRaggiunto } from '@shared/strumenti/tetto';
import { millisDa } from '@shared/strumenti/attivazione';
import type { TenantConsoleListItem } from '../../services/tenantService';
import {
  contatoreInCorso, impostaCondizioniSuMisura, leggiContatori, leggiListino, type ContatoreConPeriodo, type EsitoSuMisura,
} from '../../services/strumentiService';
import { DialogoSuMisura } from './DialogoSuMisura';
import { RegaloDellAzienda } from './RegaloDellAzienda';
import { BarraQuota } from './BarraQuota';
import { TelefonoDellAzienda } from '../telefono/TelefonoDellAzienda';
import { comePaga, condizioniDette, data, eurDaCentesimi, testoTetto, ultimoGiorno, unita } from './testiStrumenti';

interface Props {
  tenant: TenantConsoleListItem;
  /** Dopo un cambio di condizioni la scheda rilegge l'azienda. */
  onChange: () => void;
}

/** Le condizioni dell'azienda per uno strumento, e i bottoni per cambiarle. */
const CondizioniDellAzienda: React.FC<{
  tenant: TenantConsoleListItem; dalListino: StrumentoACanone; nellAbbonamento: boolean; onCambiate: (esito: EsitoSuMisura) => void;
  /** Il bottone del regalo, nella stessa riga delle azioni sulle condizioni. */
  azioniInPiu?: React.ReactNode;
}> = ({ tenant, dalListino, nellAbbonamento, onCambiate, azioniInPiu }) => {
  const [dialogo, setDialogo] = useState(false);
  const [conferma, setConferma] = useState(false);
  const [lavoro, setLavoro] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const suMisura = suMisuraDallAzienda(tenant, dalListino);
  const paga = strumentoPerLAzienda(tenant, dalListino);
  const inAttesa = (tenant as { billing?: { su_misura?: Record<string, { in_attesa_del_rinnovo?: boolean }> } }).billing?.su_misura?.[dalListino.capability]?.in_attesa_del_rinnovo === true;
  const a = suMisura.attuale;
  const diversoDalListino = paga.canone_mensile_eur !== dalListino.canone_mensile_eur || JSON.stringify(paga.consumo) !== JSON.stringify(dalListino.consumo);

  const togli = async () => {
    setLavoro(true);
    setErrore(null);
    try {
      onCambiate(await impostaCondizioniSuMisura({ tenant_id: tenant.id, strumento: dalListino.capability, condizioni: null }));
      setConferma(false);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    } finally {
      setLavoro(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {a ? <SoftChip label="su misura" tone="gold" /> : <SoftChip label={diversoDalListino ? 'prezzo di prima' : 'listino'} tone="default" />}
        <Typography variant="body2" sx={{ color: tokens.color.graphite }}>
          {a
            ? `${a.dal_ms ? `dal ${data(a.dal_ms)}: ` : ''}${condizioniDette(a)}`
            : condizioniDette(paga)}.
        </Typography>
      </Stack>
      {a?.nota && <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.ash }}>Perché: {a.nota}</Typography>}
      {inAttesa && (
        <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.warning }}>
          Passa a queste condizioni dopo il rinnovo, perché nel mese ha già speso in extra. Intanto paga così: {condizioniDette(paga)}.
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" variant="outlined" onClick={() => setDialogo(true)} sx={{ textTransform: 'none', minHeight: 36 }}>
          {a ? 'Cambia le condizioni' : 'Condizioni su misura'}
        </Button>
        {a && (
          <Button size="small" onClick={() => setConferma(true)} sx={{ textTransform: 'none', minHeight: 36, color: tokens.color.ash }}>
            Torna al listino
          </Button>
        )}
        {azioniInPiu}
      </Stack>
      {errore && <Alert severity="error" sx={{ mt: 1 }}>{errore}</Alert>}

      <DialogoSuMisura
        aperto={dialogo}
        onChiudi={() => setDialogo(false)}
        onSalvato={(e) => { setDialogo(false); onCambiate(e); }}
        tenantId={tenant.id}
        nomeAzienda={tenant.nome_azienda}
        listino={dalListino}
        attuali={paga}
        notaAttuale={a?.nota ?? ''}
        nellAbbonamento={nellAbbonamento}
      />
      <Dialog open={conferma} onClose={lavoro ? undefined : () => setConferma(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: tokens.font.display, fontWeight: 600 }}>Torna al listino</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {tenant.nome_azienda} pagherà {dalListino.etichetta} come tutte: {condizioniDette(dalListino)}.
            {nellAbbonamento ? ' Lo ha già nell’abbonamento: passa subito (o dopo il rinnovo, se nel mese ha già speso in extra).' : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConferma(false)} disabled={lavoro}>Annulla</Button>
          <Button variant="contained" disableElevation onClick={() => { void togli(); }} disabled={lavoro}
            startIcon={lavoro ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Torna al listino
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const etichettaCampo = { variant: 'caption' as const, sx: { color: tokens.color.ash, display: 'block' } };

/**
 * Condizioni e regalo di uno strumento: senza regalo, «Regala» sta nella riga delle azioni; con un regalo in corso, il
 * regalo ha il suo blocco (fino a quando, perché, «Togli il regalo»).
 */
const ConCondizioniERegalo: React.FC<{
  tenant: TenantConsoleListItem; strumento: StrumentoACanone; nellAbbonamento: boolean;
  onCambiate: (esito: EsitoSuMisura) => void; onRegalo: () => void;
}> = ({ tenant, strumento, nellAbbonamento, onCambiate, onRegalo }) => {
  const conRegalo = regaloDallAzienda(tenant, strumento.capability, Date.now()) !== null;
  const regalo = <RegaloDellAzienda tenant={tenant} strumento={strumento} nellAbbonamento={nellAbbonamento} onCambiato={onRegalo} />;
  return (
    <>
      <CondizioniDellAzienda tenant={tenant} dalListino={strumento} nellAbbonamento={nellAbbonamento} onCambiate={onCambiate} azioniInPiu={conRegalo ? null : regalo} />
      {conRegalo && regalo}
    </>
  );
};

const SchedaStrumento: React.FC<{
  tenant: TenantConsoleListItem; riga: RigaDiConsumo; dalListino: StrumentoACanone; contatori: ContatoreConPeriodo[];
  periodo: ContatoreConPeriodo | null; onCambiate: (esito: EsitoSuMisura) => void; onRegalo: () => void;
}> = ({ tenant, riga, dalListino, contatori, periodo, onCambiate, onRegalo }) => {
  const s = riga.strumento;
  const paga = comePaga(riga);
  const tetto = tettoDelloStrumento(tenant, dalListino);
  const inFattura = extraInFattura(riga);
  const alTetto = inFattura && tettoRaggiunto(s.consumo, riga.usate, tetto);
  const stato = (tenant as { billing?: { strumenti?: Record<string, { acceso_il?: unknown }> } }).billing?.strumenti?.[s.capability];
  const accesoIl = millisDa(stato?.acceso_il);
  // I servizi solo a consumo non hanno extra «oltre la quota»: tutta la spesa è consumo, e il tetto è sulla spesa.
  const aConsumo = soloAConsumo(s);
  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line, borderRadius: `${tokens.radius.lg}px` }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mr: 1 }}>{s.etichetta}</Typography>
        <SoftChip label={paga.testo} tone={paga.tono} />
        {accesoIl && <Typography variant="body2" sx={{ color: tokens.color.ash }}>nell’abbonamento dal {data(accesoIl)}</Typography>}
      </Stack>
      <ConCondizioniERegalo tenant={tenant} strumento={dalListino} nellAbbonamento={accesoIl !== null} onCambiate={onCambiate} onRegalo={onRegalo} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 3, mt: 2.5, alignItems: 'start' }}>
        <Box>
          <Typography {...etichettaCampo}>
            {periodo ? `Periodo dal ${data(periodo.inizio_ms)} al ${ultimoGiorno(periodo.fine_ms)}` : 'Periodo in corso: niente consumato'}
          </Typography>
          <Box sx={{ mt: 1 }}><BarraQuota usate={riga.usate} quota={riga.quota} unitaDiConsumo={s.consumo.unita} tettoEur={tetto.tipo === 'nessuno' ? null : tetto.eur} /></Box>
        </Box>
        <Box>
          <Typography {...etichettaCampo}>{aConsumo ? 'Spesa finora' : 'Extra finora'}</Typography>
          <Typography sx={{ fontFamily: tokens.font.display, fontSize: '1.6rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: inFattura ? tokens.color.graphite : tokens.color.mist }}>
            {eurDaCentesimi(riga.extra_centesimi)}
          </Typography>
          {!inFattura && <Typography variant="caption" sx={{ color: tokens.color.mist }}>non arriva in fattura</Typography>}
        </Box>
        <Box>
          <Typography {...etichettaCampo}>{aConsumo ? 'Tetto di spesa' : 'Tetto di spesa extra'}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: alTetto ? tokens.color.danger : tokens.color.graphite, fontWeight: alTetto ? 600 : 400 }}>
            {inFattura ? `${alTetto ? 'Raggiunto: ' : ''}${testoTetto(tetto, dalListino)}` : riga.via.via === 'senza_addebito' || riga.regalato ? 'non conta: non si paga' : 'non conta finché non paga'}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.mist }}>lo sceglie l’azienda</Typography>
        </Box>
      </Box>

      {contatori.length > 0 && (
        <Table size="small" sx={{ mt: 2.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Periodo</TableCell>
              <TableCell align="right">{aConsumo ? 'Spesa' : unita(s.consumo.unita, 2)}</TableCell>
              <TableCell align="right">{aConsumo ? 'In fattura' : 'Extra stimato'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contatori.map((c) => {
              const usate = Math.floor(c.strumenti?.[s.capability]?.quantita ?? 0);
              // Il periodo com'era allora (02/11/2026): in prova o senza addebito l'extra NON è andato in fattura.
              const delPeriodo = consumoDelPeriodo(tenant, riga, c);
              return (
                <TableRow key={c.chiave}>
                  <TableCell sx={{ color: tokens.color.ash }}>{data(c.inizio_ms)} – {ultimoGiorno(c.fine_ms)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.font.mono }}>{aConsumo ? eurDaCentesimi(usate) : usate}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.font.mono, color: delPeriodo === 'in_fattura' ? tokens.color.graphite : tokens.color.mist }}>
                    {delPeriodo === 'in_fattura'
                      ? eurDaCentesimi(extraInCentesimi(s.consumo, usate))
                      : delPeriodo === 'in_prova' ? 'gratis, in prova' : 'non si paga'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export const TenantStrumentiTab: React.FC<Props> = ({ tenant, onChange }) => {
  const [listino, setListino] = useState<Map<string, StrumentoACanone> | null>(null);
  const [contatori, setContatori] = useState<ContatoreConPeriodo[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<EsitoSuMisura | null>(null);
  const cambiate = (e: EsitoSuMisura) => { setEsito(e); onChange(); };
  const ora = useMemo(() => Date.now(), []);

  useEffect(() => {
    Promise.all([leggiListino(), leggiContatori(tenant.id, 6)])
      .then(([l, c]) => { setListino(l); setContatori(c); })
      .catch((e) => setErrore(e instanceof Error ? e.message : String(e)));
  }, [tenant.id]);

  if (errore) return <Alert severity="error">{errore}</Alert>;
  if (!listino) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>;

  const inCorso = contatoreInCorso(contatori, ora);
  const righe = righeDiConsumo(tenant, listino, inCorso, ora);
  const spenti = [...listino.values()].filter((s) => !righe.some((r) => r.strumento.capability === s.capability));
  return (
    <Stack spacing={2}>
      {esito && (
        <Alert severity="success" onClose={() => setEsito(null)}>
          {esito.dopo.su_misura ? 'Condizioni su misura salvate.' : 'L’azienda è tornata al listino.'}{' '}
          {esito.passaggio === 'applicato' && 'L’abbonamento ha già i prezzi nuovi.'}
          {esito.passaggio === 'al_rinnovo' && 'Nel mese ha già speso in extra: l’abbonamento passa ai prezzi nuovi dopo il rinnovo.'}
          {esito.passaggio === 'nessun_abbonamento' && 'Varranno da quando attiva lo strumento.'}
        </Alert>
      )}
      {/* Il telefono si consegna da qui: numero assegnato, richiesta che aspetta, «Assegna il numero» (cap. 264). */}
      <TelefonoDellAzienda tenant={tenant} onChange={onChange} />
      {righe.length === 0 && <Typography sx={{ color: tokens.color.ash }}>Nessuno strumento a canone acceso.</Typography>}
      {righe.map((r) => (
        <SchedaStrumento key={r.strumento.capability} tenant={tenant} riga={r} dalListino={listino.get(r.strumento.capability)!} contatori={contatori} periodo={inCorso} onCambiate={cambiate} onRegalo={() => { setEsito(null); onChange(); }} />
      ))}
      {spenti.map((s) => (
        <Paper key={s.capability} variant="outlined" sx={{ p: 2.5, borderColor: tokens.color.line, borderRadius: `${tokens.radius.lg}px`, bgcolor: tokens.color.bone }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 700 }}>{s.etichetta}</Typography>
            <SoftChip label="spento" tone="default" />
          </Stack>
          <ConCondizioniERegalo tenant={tenant} strumento={s} nellAbbonamento={false} onCambiate={cambiate} onRegalo={() => { setEsito(null); onChange(); }} />
        </Paper>
      ))}
    </Stack>
  );
};
